import { useApi } from '@api/ApiProvider'
import { TREE_CONDITIONS, type TreeHealth } from '@api/types'
import { useI18n } from '@i18n/I18nProvider'
import { CONDITION_STYLES } from '@ui/ConditionBadge'
import { ErrorState, LoadingState } from '@ui/States'
import type { ReactNode } from 'react'
import { Link } from 'wouter'
import { ParticipationChart } from './ParticipationChart'
import { lastDays, lastWeeks } from './period'
import { usePanelQuery } from './StaffSession'

const CARD = 'rounded-3xl bg-white p-4 shadow-sm'

const Section = ({ title, hint, children }: { title: string; hint: string; children: ReactNode }) => (
	<section className="mb-8">
		<h2 className="text-2xl font-semibold">{title}</h2>
		<p className="mb-3 text-earth-800">{hint}</p>
		{children}
	</section>
)

// Cifra grande con su estado al lado (nunca solo el color). Cada estado lleva a sus árboles.
const HealthTiles = ({ health }: { health: TreeHealth }) => {
	const { t } = useI18n()
	return (
		<>
			<ul className="grid grid-cols-2 gap-3">
				{TREE_CONDITIONS.map((condition) => (
					<li key={condition}>
						<Link href={`/arboles?estado=${condition}`} className={`${CARD} block active:bg-earth-100`}>
							<span className="block text-4xl font-bold tabular-nums">{health[condition]}</span>
							<span className="flex items-center gap-2 font-semibold">
								<span
									className={`size-3 rounded-full ${CONDITION_STYLES[condition]}`}
									aria-hidden="true"
								/>
								{t(`condition.${condition}`)}
							</span>
						</Link>
					</li>
				))}
				<li className={`${CARD} col-span-2`}>
					<span className="block text-4xl font-bold tabular-nums">{health.withoutReports}</span>
					<span className="font-semibold">{t('condition.none')}</span>
				</li>
			</ul>
			<p className="mt-3 text-earth-800">{t('health.total', { count: health.total })}</p>
		</>
	)
}

// Pestaña "Resumen": estado del arbolado, participación (12 semanas) y actividad (30 días).
export const SummaryPage = ({ now = new Date() }: { now?: Date }) => {
	const { t, locale } = useI18n()
	const api = useApi()
	const weeks = lastWeeks(12, now)
	const month = lastDays(30, now)
	const health = usePanelQuery(['tree-health'], (token) => api.treeHealth(token))
	const participation = usePanelQuery(['participation', weeks], (token) => api.participation(token, weeks, 'week'))
	const activity = usePanelQuery(['activity', month, locale], (token) =>
		api.activityByMunicipality(token, month, locale)
	)

	return (
		<>
			<Section title={t('health.title')} hint={t('health.hint')}>
				{health.isPending ? (
					<LoadingState shape="cards" />
				) : health.data ? (
					<HealthTiles health={health.data} />
				) : (
					<ErrorState error={health.error} onRetry={() => health.refetch()} />
				)}
			</Section>

			<Section title={t('participation.title')} hint={t('participation.hint')}>
				{participation.isPending ? (
					<LoadingState shape="cards" />
				) : participation.data ? (
					<div className={CARD}>
						{participation.data.points.some((p) => p.reports > 0) ? (
							<ParticipationChart points={participation.data.points} />
						) : (
							<p className="py-4 text-center text-earth-800">{t('participation.empty')}</p>
						)}
					</div>
				) : (
					<ErrorState error={participation.error} onRetry={() => participation.refetch()} />
				)}
			</Section>

			<Section title={t('activity.title')} hint={t('activity.hint')}>
				{activity.isPending ? (
					<LoadingState shape="cards" />
				) : activity.data ? (
					activity.data.items.length === 0 ? (
						<p className={`${CARD} text-center text-earth-800`}>{t('activity.empty')}</p>
					) : (
						<div className={`${CARD} overflow-x-auto`}>
							<table className="w-full text-left">
								<thead>
									<tr className="border-b border-earth-300 text-sm">
										<th className="py-2 font-semibold">{t('activity.municipality')}</th>
										<th className="py-2 text-right font-semibold">{t('participation.reports')}</th>
										<th className="py-2 text-right font-semibold">
											{t('participation.participants')}
										</th>
										<th className="py-2 text-right font-semibold">{t('participation.trees')}</th>
									</tr>
								</thead>
								<tbody>
									{[...activity.data.items]
										.sort((a, b) => b.reports - a.reports)
										.map((row) => (
											<tr key={row.municipalityId} className="border-b border-earth-100">
												<td className="py-2 pr-2">
													{row.municipalityName ?? t('activity.unknown')}
												</td>
												<td className="py-2 text-right tabular-nums">{row.reports}</td>
												<td className="py-2 text-right tabular-nums">{row.participants}</td>
												<td className="py-2 text-right tabular-nums">{row.trees}</td>
											</tr>
										))}
								</tbody>
							</table>
						</div>
					)
				) : (
					<ErrorState error={activity.error} onRetry={() => activity.refetch()} />
				)}
			</Section>
		</>
	)
}
